package com.ragnorak.clanybackoffice.ui.screens

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dev.zacsweers.metro.Inject
import com.clany.backoffice.data.network.response.AdvancedNotificationFilter
import com.clany.backoffice.domain.usecase.GetUsersUseCase
import com.clany.backoffice.domain.usecase.UpdateUserUseCase
import com.clany.backoffice.domain.usecase.DeleteUserUseCase
import com.clany.backoffice.domain.repository.UserRepository
import com.clany.backoffice.model.UserResponse
import kotlinx.coroutines.launch

data class UserFilterState(
    // User profile
    val selectedUserTypes: Set<String> = emptySet(),
    val selectedUserStatuses: Set<String> = emptySet(),
    val localesRaw: String = "",
    val selectedAuthProviders: Set<String> = emptySet(),
    val appVersionsRaw: String = "",
    val isAnonymized: TriStateOption = TriStateOption.ANY,
    val shouldUpdateTyc: TriStateOption = TriStateOption.ANY,
    val hasLinkedMember: TriStateOption = TriStateOption.ANY,
    val createdAfter: String = "",
    val createdBefore: String = "",
    val lastActiveAfter: String = "",
    val lastActiveBefore: String = "",
    // Device
    val selectedPlatforms: Set<String> = emptySet(),
    val hasDeviceTokens: TriStateOption = TriStateOption.ANY,
    // Accounts
    val hasAccounts: TriStateOption = TriStateOption.ANY,
    val minAccountCount: String = "",
    val maxAccountCount: String = "",
    val selectedAccountRoles: Set<String> = emptySet(),
    val selectedAccountUserStatuses: Set<String> = emptySet(),
    // Subscriptions
    val hasActiveSubscription: TriStateOption = TriStateOption.ANY,
    val selectedSubscriptionPlans: Set<String> = emptySet(),
    val selectedSubscriptionStatuses: Set<String> = emptySet(),
    // Members
    val isMember: TriStateOption = TriStateOption.ANY,
    val minMemberCount: String = "",
    val maxMemberCount: String = "",
    // Events
    val hasEvents: TriStateOption = TriStateOption.ANY,
    val minEventCount: String = "",
    val maxEventCount: String = "",
    val selectedEventTypes: Set<String> = emptySet(),
    // Invitations
    val hasSentInvitations: TriStateOption = TriStateOption.ANY,
    val hasReceivedInvitations: TriStateOption = TriStateOption.ANY,
    val selectedInvitationStatuses: Set<String> = emptySet(),
    val minSentInvitations: String = "",
    val maxSentInvitations: String = "",
    val minReceivedInvitations: String = "",
    val maxReceivedInvitations: String = "",
    // Specific IDs
    val includeUserIdsRaw: String = "",
    val excludeUserIdsRaw: String = "",
)

data class UsersUiState(
    val isLoading: Boolean = false,
    val users: List<UserResponse> = emptyList(),
    val error: String? = null,
    val page: Int = 1,
    val hasMore: Boolean = true,
    val editingUser: UserResponse? = null,
    val deletingUserId: String? = null,
    val searchQuery: String = "",
    val viewingUser: UserResponse? = null,
    // Advanced filter
    val filterState: UserFilterState = UserFilterState(),
    val isFilterPanelOpen: Boolean = false,
    val isFilterActive: Boolean = false,
    val totalFilterCount: Int? = null,
)

class UsersViewModel @Inject constructor(
    private val getUsersUseCase: GetUsersUseCase,
    private val updateUserUseCase: UpdateUserUseCase,
    private val deleteUserUseCase: DeleteUserUseCase,
    private val userRepository: UserRepository
) : ViewModel() {

    var uiState by mutableStateOf(UsersUiState())
        private set

    init {
        loadUsers()
    }

    fun loadUsers(page: Int = 1) {
        if (uiState.isFilterActive) {
            applyFilter(page = page)
            return
        }
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            getUsersUseCase(page = page, pageSize = 20)
                .onSuccess { users ->
                    val filtered = if (uiState.searchQuery.isNotBlank()) {
                        users.filter { user ->
                            val q = uiState.searchQuery.lowercase()
                            user.firstName.lowercase().contains(q) ||
                            user.lastName.lowercase().contains(q) ||
                            user.email.lowercase().contains(q) ||
                            user.username.lowercase().contains(q)
                        }
                    } else users

                    uiState = uiState.copy(
                        isLoading = false,
                        users = if (page == 1) filtered else uiState.users + filtered,
                        page = page,
                        hasMore = users.isNotEmpty()
                    )
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    fun onSearchQueryChanged(query: String) {
        uiState = uiState.copy(searchQuery = query)
        if (!uiState.isFilterActive) {
            loadUsers(page = 1)
        }
    }

    fun onEditUser(user: UserResponse) {
        uiState = uiState.copy(editingUser = user)
    }

    fun dismissEdit() {
        uiState = uiState.copy(editingUser = null)
    }

    fun onViewUser(user: UserResponse) {
        uiState = uiState.copy(viewingUser = user)
    }

    fun dismissView() {
        uiState = uiState.copy(viewingUser = null)
    }

    fun updateUser(user: UserResponse) {
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true)
            updateUserUseCase(user)
                .onSuccess { updatedUser ->
                    val updatedList = uiState.users.map { if (it.id == updatedUser.id) updatedUser else it }
                    uiState = uiState.copy(isLoading = false, users = updatedList, editingUser = null)
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    fun onDeleteUser(userId: String) {
        uiState = uiState.copy(deletingUserId = userId)
    }

    fun dismissDelete() {
        uiState = uiState.copy(deletingUserId = null)
    }

    fun confirmDelete() {
        val userId = uiState.deletingUserId ?: return
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true)
            deleteUserUseCase(userId)
                .onSuccess {
                    val updatedList = uiState.users.filter { it.id != userId }
                    uiState = uiState.copy(isLoading = false, users = updatedList, deletingUserId = null)
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    // ── Filter panel ──────────────────────────────────────────────────────────

    fun toggleFilterPanel() {
        uiState = uiState.copy(isFilterPanelOpen = !uiState.isFilterPanelOpen)
    }

    fun updateUserFilter(update: (UserFilterState) -> UserFilterState) {
        uiState = uiState.copy(filterState = update(uiState.filterState))
    }

    fun applyFilter(page: Int = 1) {
        val filter = buildAdvancedFilter()
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            userRepository.filterUsers(filter = filter, page = page, limit = 20)
                .onSuccess { users ->
                    uiState = uiState.copy(
                        isLoading = false,
                        users = if (page == 1) users else uiState.users + users,
                        page = page,
                        hasMore = users.isNotEmpty(),
                        isFilterActive = true,
                        isFilterPanelOpen = false
                    )
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    fun clearFilter() {
        uiState = uiState.copy(
            filterState = UserFilterState(),
            isFilterActive = false,
            totalFilterCount = null,
            isFilterPanelOpen = false
        )
        loadUsers(page = 1)
    }

    // ── Individual filter update methods ──────────────────────────────────────

    private fun <T> Set<T>.toggle(item: T): Set<T> =
        if (contains(item)) this - item else this + item

    fun toggleUserType(type: String) =
        updateUserFilter { it.copy(selectedUserTypes = it.selectedUserTypes.toggle(type)) }

    fun toggleUserStatus(status: String) =
        updateUserFilter { it.copy(selectedUserStatuses = it.selectedUserStatuses.toggle(status)) }

    fun updateLocalesRaw(value: String) =
        updateUserFilter { it.copy(localesRaw = value) }

    fun toggleAuthProvider(provider: String) =
        updateUserFilter { it.copy(selectedAuthProviders = it.selectedAuthProviders.toggle(provider)) }

    fun updateAppVersionsRaw(value: String) =
        updateUserFilter { it.copy(appVersionsRaw = value) }

    fun setIsAnonymized(option: TriStateOption) =
        updateUserFilter { it.copy(isAnonymized = option) }

    fun setShouldUpdateTyc(option: TriStateOption) =
        updateUserFilter { it.copy(shouldUpdateTyc = option) }

    fun setHasLinkedMember(option: TriStateOption) =
        updateUserFilter { it.copy(hasLinkedMember = option) }

    fun updateCreatedAfter(value: String) =
        updateUserFilter { it.copy(createdAfter = value) }

    fun updateCreatedBefore(value: String) =
        updateUserFilter { it.copy(createdBefore = value) }

    fun updateLastActiveAfter(value: String) =
        updateUserFilter { it.copy(lastActiveAfter = value) }

    fun updateLastActiveBefore(value: String) =
        updateUserFilter { it.copy(lastActiveBefore = value) }

    fun togglePlatform(platform: String) =
        updateUserFilter { it.copy(selectedPlatforms = it.selectedPlatforms.toggle(platform)) }

    fun setHasDeviceTokens(option: TriStateOption) =
        updateUserFilter { it.copy(hasDeviceTokens = option) }

    fun setHasAccounts(option: TriStateOption) =
        updateUserFilter { it.copy(hasAccounts = option) }

    fun updateMinAccountCount(value: String) =
        updateUserFilter { it.copy(minAccountCount = value) }

    fun updateMaxAccountCount(value: String) =
        updateUserFilter { it.copy(maxAccountCount = value) }

    fun toggleAccountRole(role: String) =
        updateUserFilter { it.copy(selectedAccountRoles = it.selectedAccountRoles.toggle(role)) }

    fun toggleAccountUserStatus(status: String) =
        updateUserFilter { it.copy(selectedAccountUserStatuses = it.selectedAccountUserStatuses.toggle(status)) }

    fun setHasActiveSubscription(option: TriStateOption) =
        updateUserFilter { it.copy(hasActiveSubscription = option) }

    fun toggleSubscriptionPlan(plan: String) =
        updateUserFilter { it.copy(selectedSubscriptionPlans = it.selectedSubscriptionPlans.toggle(plan)) }

    fun toggleSubscriptionStatus(status: String) =
        updateUserFilter { it.copy(selectedSubscriptionStatuses = it.selectedSubscriptionStatuses.toggle(status)) }

    fun setIsMember(option: TriStateOption) =
        updateUserFilter { it.copy(isMember = option) }

    fun updateMinMemberCount(value: String) =
        updateUserFilter { it.copy(minMemberCount = value) }

    fun updateMaxMemberCount(value: String) =
        updateUserFilter { it.copy(maxMemberCount = value) }

    fun setHasEvents(option: TriStateOption) =
        updateUserFilter { it.copy(hasEvents = option) }

    fun updateMinEventCount(value: String) =
        updateUserFilter { it.copy(minEventCount = value) }

    fun updateMaxEventCount(value: String) =
        updateUserFilter { it.copy(maxEventCount = value) }

    fun toggleEventType(type: String) =
        updateUserFilter { it.copy(selectedEventTypes = it.selectedEventTypes.toggle(type)) }

    fun setHasSentInvitations(option: TriStateOption) =
        updateUserFilter { it.copy(hasSentInvitations = option) }

    fun setHasReceivedInvitations(option: TriStateOption) =
        updateUserFilter { it.copy(hasReceivedInvitations = option) }

    fun toggleInvitationStatus(status: String) =
        updateUserFilter { it.copy(selectedInvitationStatuses = it.selectedInvitationStatuses.toggle(status)) }

    fun updateMinSentInvitations(value: String) =
        updateUserFilter { it.copy(minSentInvitations = value) }

    fun updateMaxSentInvitations(value: String) =
        updateUserFilter { it.copy(maxSentInvitations = value) }

    fun updateMinReceivedInvitations(value: String) =
        updateUserFilter { it.copy(minReceivedInvitations = value) }

    fun updateMaxReceivedInvitations(value: String) =
        updateUserFilter { it.copy(maxReceivedInvitations = value) }

    fun updateIncludeUserIdsRaw(value: String) =
        updateUserFilter { it.copy(includeUserIdsRaw = value) }

    fun updateExcludeUserIdsRaw(value: String) =
        updateUserFilter { it.copy(excludeUserIdsRaw = value) }

    // ── Build advanced filter ─────────────────────────────────────────────────

    private fun buildAdvancedFilter(): AdvancedNotificationFilter {
        val fs = uiState.filterState

        val includeIds = fs.includeUserIdsRaw
            .lines()
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .takeIf { it.isNotEmpty() }

        val excludeIds = fs.excludeUserIdsRaw
            .lines()
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .takeIf { it.isNotEmpty() }

        val locales = fs.localesRaw
            .split(",")
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .takeIf { it.isNotEmpty() }

        val appVersions = fs.appVersionsRaw
            .splitToSequence(",")
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .toList()
            .takeIf { it.isNotEmpty() }

        return AdvancedNotificationFilter(
            includeUserIds = includeIds,
            excludeUserIds = excludeIds,
            userTypes = fs.selectedUserTypes.toList().takeIf { it.isNotEmpty() },
            userStatuses = fs.selectedUserStatuses.toList().takeIf { it.isNotEmpty() },
            locales = locales,
            authProviders = fs.selectedAuthProviders.toList().takeIf { it.isNotEmpty() },
            appVersions = appVersions,
            isAnonymized = fs.isAnonymized.toNullableBoolean(),
            shouldUpdateTyc = fs.shouldUpdateTyc.toNullableBoolean(),
            hasLinkedMember = fs.hasLinkedMember.toNullableBoolean(),
            createdAfter = epochFromDateString(fs.createdAfter),
            createdBefore = epochFromDateString(fs.createdBefore),
            lastActiveAfter = epochFromDateString(fs.lastActiveAfter),
            lastActiveBefore = epochFromDateString(fs.lastActiveBefore),
            platforms = fs.selectedPlatforms.toList().takeIf { it.isNotEmpty() },
            hasDeviceTokens = fs.hasDeviceTokens.toNullableBoolean(),
            hasAccounts = fs.hasAccounts.toNullableBoolean(),
            minAccountCount = fs.minAccountCount.toIntOrNull(),
            maxAccountCount = fs.maxAccountCount.toIntOrNull(),
            accountRoles = fs.selectedAccountRoles.toList().takeIf { it.isNotEmpty() },
            accountUserStatuses = fs.selectedAccountUserStatuses.toList().takeIf { it.isNotEmpty() },
            hasActiveSubscription = fs.hasActiveSubscription.toNullableBoolean(),
            subscriptionPlans = fs.selectedSubscriptionPlans.toList().takeIf { it.isNotEmpty() },
            subscriptionStatuses = fs.selectedSubscriptionStatuses.toList().takeIf { it.isNotEmpty() },
            isMember = fs.isMember.toNullableBoolean(),
            minMemberCount = fs.minMemberCount.toIntOrNull(),
            maxMemberCount = fs.maxMemberCount.toIntOrNull(),
            hasEvents = fs.hasEvents.toNullableBoolean(),
            minEventCount = fs.minEventCount.toIntOrNull(),
            maxEventCount = fs.maxEventCount.toIntOrNull(),
            eventTypes = fs.selectedEventTypes.toList().takeIf { it.isNotEmpty() },
            hasSentInvitations = fs.hasSentInvitations.toNullableBoolean(),
            hasReceivedInvitations = fs.hasReceivedInvitations.toNullableBoolean(),
            invitationStatuses = fs.selectedInvitationStatuses.toList().takeIf { it.isNotEmpty() },
            minSentInvitations = fs.minSentInvitations.toIntOrNull(),
            maxSentInvitations = fs.maxSentInvitations.toIntOrNull(),
            minReceivedInvitations = fs.minReceivedInvitations.toIntOrNull(),
            maxReceivedInvitations = fs.maxReceivedInvitations.toIntOrNull()
        )
    }

    // ── Date helper ───────────────────────────────────────────────────────────

    private fun epochFromDateString(s: String): Long? {
        if (s.isBlank()) return null
        return try {
            val parts = s.trim().split("-")
            if (parts.size != 3) return null
            val year = parts[0].toInt()
            val month = parts[1].toInt()
            val day = parts[2].toInt()
            val daysToYear = daysFromEpochToYear(year)
            val daysToMonth = daysInMonthsBefore(month, year)
            (daysToYear + daysToMonth + day - 1) * 86400_000L
        } catch (e: Exception) {
            null
        }
    }

    private fun isLeapYear(year: Int) = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0

    private fun daysFromEpochToYear(year: Int): Long {
        var days = 0L
        for (y in 1970 until year) days += if (isLeapYear(y)) 366L else 365L
        return days
    }

    private fun daysInMonthsBefore(month: Int, year: Int): Int {
        val days = intArrayOf(31, if (isLeapYear(year)) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
        return days.take(month - 1).sum()
    }
}
